import { FC, useState, useEffect, useMemo } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Autocomplete,
  Grid,
  Box,
  Typography,
} from '@mui/material'
import { FormElement, Variable, MobileListItem } from '@mobileTypes'
import { useMobileListItems } from '@mobileHooks'
import { VariableAutocompleteWithCreate } from '@mobileComponents/Variables'
import { InternalIdInput } from '@library'

interface ListItemConfigDialogProps {
  open: boolean
  initialData?: Partial<FormElement>
  variables: Variable[]
  onClose: () => void
  onSave: (config: {
    internalId?: string
    listItemConfigId?: string
    listItemDataVariable?: string
  }) => void
  onCreateVariable?: (name: string, isGlobal: boolean, valueType?: string, variableType?: string) => void
}

export const ListItemConfigDialog: FC<ListItemConfigDialogProps> = ({
                                                                      open,
                                                                      initialData,
                                                                      variables,
                                                                      onClose,
                                                                      onSave,
                                                                      onCreateVariable,
                                                                    }) => {
  const { listItems } = useMobileListItems()

  const [internalId, setInternalId] = useState<string>(initialData?.internalId ?? '')
  const [selectedListItemId, setSelectedListItemId] = useState<string | null>(
    initialData?.listItemConfigId || null,
  )
  const [dataVariable, setDataVariable] = useState<string>(
    initialData?.listItemDataVariable || '',
  )

  useEffect(() => {
    if (open) {
      setInternalId(initialData?.internalId ?? '')
    }
  }, [open, initialData?.internalId])

  const selectedListItem = useMemo(() => {
    if (!selectedListItemId) return null
    return listItems.find(item => item.internalId === selectedListItemId) || null
  }, [selectedListItemId, listItems])

  const handleSave = () => {
    if (!selectedListItemId) {
      return
    }

    onSave({
      internalId: internalId.trim() || undefined,
      listItemConfigId: selectedListItemId,
      listItemDataVariable: dataVariable || undefined,
    })
    onClose()
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Configure List Item</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12}>
            <InternalIdInput
              value={internalId}
              onChange={setInternalId}
              fullWidth
              label="Internal ID"
              helperText="Unique ID for references"
            />
          </Grid>
          <Grid item xs={12}>
            <Autocomplete
              options={listItems}
              getOptionLabel={(option) => option.name}
              value={selectedListItem}
              onChange={(_, newValue) => {
                setSelectedListItemId(newValue?.internalId || null)
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select List Item Configuration"
                  helperText="Choose an existing list item configuration to display"
                  required
                />
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <VariableAutocompleteWithCreate
              options={variables}
              valueInternalId={dataVariable}
              onChangeInternalId={setDataVariable}
              label="Data Variable (optional)"
              helperText="Variable containing the item data to display"
              onCreateVariable={onCreateVariable}
              createDialogTitle="Create Data Variable"
              createDialogDescription="Create a variable to store the item data."
              showScopeSelector
              defaultScope="local"
              defaultValueType="object"
              showValueTypeSelector={false}
            />
          </Grid>

          <Grid item xs={12}>
            <Box sx={{ p: 2, bgcolor: 'info.50', borderRadius: 1, border: '1px solid', borderColor: 'info.main' }}>
              <Typography variant="caption" color="info.main">
                💡 The List Item element displays a single item using the selected List Item configuration.
                Pass item data via the data variable or through navigation params.
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" disabled={!selectedListItemId}>
          Save
        </Button>
      </DialogActions>

    </Dialog>
  )
}
